import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAttendanceStatus, useAttendanceTeam, useDashboard, useRanking, useRemoveUser, useSaveUser, useUsers } from '@/hooks/queries';
import { FasTeamModal } from '@/components/attendance/FasTeamModal';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import {
  Avatar, ConfirmDialog, EmptyState, ErrorState, Field, Modal, MoneyInput, ProgressBar, TableSkeleton,
} from '@/components/ui';
import { IconCheck, IconEdit, IconSearch, IconTrash, IconUserPlus, IconUsers } from '@/components/Icons';
import {
  formatBRLShort, formatDate, formatDuration, formatNumber, formatPercent, formatRate, ROLE_LABELS,
} from '@/lib/format';
import { PasswordField } from '@/components/PasswordField';
import { isValidLogin, suggestLogin } from '@/lib/login';
import { isStrongPassword, MIN_PASSWORD_LENGTH } from '@/lib/password';
import type { Role, User } from '@/lib/types';
import { CURRENT_YEAR, YEAR_OPTIONS } from '@/lib/years';

const ROLE_OPTIONS: Role[] = ['MEMBER', 'ADMIN'];

/**
 * Gestão de membros (só administradores): acesso, papel e o desempenho
 * individual de cada pessoa no ano.
 */
export function MembersPage() {
  const { user: me } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [fasOpen, setFasOpen] = useState(false);
  const { data: fasStatus } = useAttendanceStatus();
  // Atendimento do mês na For All Sales, por membro vinculado — só quando a leitura está ligada.
  const { data: attendance } = useAttendanceTeam({ period: 'month' }, { enabled: Boolean(fasStatus?.enabled) });
  const attendanceById = new Map((attendance?.members ?? []).map((row) => [row.userId, row]));
  const showAttendance = Boolean(fasStatus?.enabled);

  const [search, setSearch] = useState('');
  const [year, setYear] = useState(CURRENT_YEAR);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<User | 'new' | null>(null);
  // Aberto pela coluna da meta: o foco vai direto para o campo dela.
  const [focusGoal, setFocusGoal] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<User | null>(null);

  const { data: users, isLoading, error } = useUsers({
    search: search || undefined,
    includeInactive: includeInactive ? 'true' : undefined,
  });
  const { data: dashboard } = useDashboard({ year });
  // A meta é mensal: o progresso é sempre o do mês corrente, qualquer que seja o ano escolhido.
  const { data: monthRanking } = useRanking({ period: 'month', limit: 200 });

  const saveUser = useSaveUser();
  const removeUser = useRemoveUser();

  function openEditor(user: User | 'new', goalFirst = false) {
    setFocusGoal(goalFirst);
    setEditing(user);
  }

  const monthSoldById = new Map((monthRanking?.entries ?? []).map((entry) => [entry.member.id, entry.soldTotal]));
  const performanceById = new Map((dashboard?.memberRanking ?? []).map((row) => [row.id, row]));
  const rows = (users ?? [])
    .map((user) => ({ user, stats: performanceById.get(user.id) }))
    .sort((a, b) => (b.stats?.soldTotal ?? 0) - (a.stats?.soldTotal ?? 0));

  const maxTotal = Math.max(1, ...rows.map((row) => row.stats?.soldTotal ?? 0));

  async function confirmRemove() {
    if (!pendingRemove) return;
    try {
      const result = await removeUser.mutateAsync(pendingRemove.id);
      toast.success(result.deleted ? 'Membro excluído.' : 'Membro desativado (o histórico foi preservado).');
      setPendingRemove(null);
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  if (error) return <ErrorState message={(error as Error).message} />;

  return (
    <>
      <h1 className="page-title">Membros</h1>
      <p className="page-lede">
        Quem tem acesso à plataforma e quanto cada pessoa vendeu no ano. Clique num membro para ver tudo do atendimento dele.
      </p>

      <div className="toolbar">
        <div className="search-input toolbar-search">
          <span className="search-input-icon" aria-hidden="true"><IconSearch /></span>
          <input
            className="inp"
            aria-label="Buscar membros"
            placeholder="Buscar por nome, usuário ou e-mail"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <select className="inp" value={year} onChange={(event) => setYear(Number(event.target.value))} aria-label="Ano">
          {YEAR_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>

        <label className="chip">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(event) => setIncludeInactive(event.target.checked)}
          />
          Mostrar inativos
        </label>

        <div className="spacer" />
        <button type="button" className="btn" onClick={() => setFasOpen(true)} title="Equipe da conta da operação na For All Sales">
          <IconUsers /> For All Sales
          {fasStatus?.enabled && fasStatus.counts.attendants > fasStatus.counts.linked ? (
            <span className="chip chip-warning" style={{ marginLeft: 2 }}>{fasStatus.counts.attendants - fasStatus.counts.linked} sem vínculo</span>
          ) : null}
        </button>
        <button type="button" className="btn btn-primary" onClick={() => openEditor('new')}>
          <IconUserPlus /> Novo membro
        </button>
      </div>

      {isLoading && !users ? (
        <TableSkeleton rows={6} columns={7} />
      ) : rows.length === 0 ? (
        <EmptyState message="Nenhum membro encontrado." />
      ) : (
        <div className="table-wrap">
          <table className="tbl tbl-cards">
            <thead>
              <tr>
                <th style={{ width: 36 }}>#</th>
                <th>Membro</th>
                <th>Papel</th>
                <th className="right">Comprovantes</th>
                <th className="right">Vendido</th>
                {/* Logo depois do vendido: a tabela passa da tela em notebooks, e a meta precisa estar à vista. */}
                <th style={{ width: 130 }} title={monthRanking ? `Vendido em ${monthRanking.period.label}, contra a meta mensal` : undefined}>
                  Meta do mês
                </th>
                {showAttendance ? (
                  <th title="Na For All Sales, este mês: leads recebidos, taxa de resposta e 1ª resposta (mediana)">Atendimento no mês</th>
                ) : null}
                <th className="right">Alavancagem</th>
                <th className="right">Repasse</th>
                <th className="right">Ticket médio</th>
                <th style={{ width: 150 }}>Participação</th>
                <th style={{ width: 70 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map(({ user, stats }, index) => (
                <tr
                  key={user.id}
                  className="row-hover"
                  style={{ cursor: 'pointer' }}
                  title={`Ver o atendimento de ${user.name}`}
                  onClick={(event) => {
                    // Botões e campos da linha continuam fazendo o que fazem.
                    if ((event.target as HTMLElement).closest('button, a, input, select')) return;
                    navigate(`/membros/${user.id}`);
                  }}
                >
                  <td className="num muted cell-hide-sm">{index + 1}</td>
                  <td className="cell-main">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <Avatar name={user.name} color={user.avatarColor} photo={user.photoUrl} size={24} />
                      <span style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 500 }}>
                          {user.name}
                          {user.id === me?.id ? <span className="muted" style={{ fontWeight: 400 }}> · você</span> : null}
                        </div>
                        <div className="muted" style={{ fontSize: 12 }}>
                          {user.login}
                          {user.email ? ` · ${user.email}` : ''}
                        </div>
                      </span>
                      {!user.active ? <span className="chip chip-negative">inativo</span> : null}
                    </span>
                  </td>
                  <td className="cell-third" data-label="Papel"><span className="chip">{ROLE_LABELS[user.role]}</span></td>
                  <td className="num right cell-third" data-label="Comprovantes">{formatNumber(stats?.receiptCount ?? 0)}</td>
                  <td className="num right emphasis cell-key" style={{ fontWeight: 500 }} data-label="Vendido">{formatBRLShort(stats?.soldTotal ?? 0)}</td>
                  <td data-label="Meta do mês">
                    <GoalCell
                      goal={user.monthlyGoal}
                      sold={monthSoldById.get(user.id) ?? 0}
                      onEdit={() => openEditor(user, true)}
                    />
                  </td>
                  {showAttendance ? <td data-label="Atendimento no mês"><AttendanceCell row={attendanceById.get(user.id)} /></td> : null}
                  <td className="num right cell-third" data-label="Alavancagem">{formatBRLShort(stats?.leverageTotal ?? 0)}</td>
                  <td className="num right positive cell-third" data-label="Repasse">{formatBRLShort(stats?.transferTotal ?? 0)}</td>
                  <td className="num right cell-third" data-label="Ticket médio">{formatBRLShort(stats?.averageTicket ?? 0)}</td>
                  <td className="cell-third" data-label="Participação">
                    <div className="bar-track" style={{ height: 14 }}>
                      <div
                        className="bar-fill"
                        style={{ width: `${((stats?.soldTotal ?? 0) / maxTotal) * 100}%`, background: user.avatarColor }}
                      />
                    </div>
                    <div className="muted num" style={{ fontSize: 11, marginTop: 3 }}>
                      {formatPercent(stats?.share ?? 0, 1)}
                    </div>
                  </td>
                  <td className="cell-actions">
                    <span style={{ display: 'inline-flex', gap: 2 }}>
                      <button type="button" className="icon-btn" title="Editar membro" onClick={() => openEditor(user)}>
                        <IconEdit />
                      </button>
                      {user.id !== me?.id ? (
                        <button type="button" className="icon-btn" title="Remover ou desativar" onClick={() => setPendingRemove(user)}>
                          <IconTrash />
                        </button>
                      ) : null}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="muted" style={{ fontSize: 12, marginTop: 14 }}>
        As métricas consideram o ano de {year}; a meta, o mês corrente
        {monthRanking ? ` (${monthRanking.period.label.toLowerCase()})` : ''}. Última entrada de cada membro e demais
        detalhes ficam no ranking.
      </p>

      {editing ? (
        <MemberModal
          member={editing === 'new' ? null : editing}
          focusGoal={focusGoal}
          busy={saveUser.isPending}
          onClose={() => setEditing(null)}
          onSubmit={async (payload) => {
            try {
              await saveUser.mutateAsync(payload);
              toast.success(editing === 'new' ? 'Membro cadastrado.' : 'Membro atualizado.');
              setEditing(null);
            } catch (caught) {
              toast.fromError(caught);
            }
          }}
        />
      ) : null}

      {fasOpen ? <FasTeamModal onClose={() => setFasOpen(false)} /> : null}

      {pendingRemove ? (
        <ConfirmDialog
          title="Remover membro"
          danger
          confirmLabel="Remover"
          busy={removeUser.isPending}
          message={
            <>
              Remover <strong>{pendingRemove.name}</strong>? Se houver comprovantes ou clientes vinculados,
              a conta é <strong>desativada</strong> em vez de excluída, preservando o histórico e o ranking.
            </>
          }
          onConfirm={() => void confirmRemove()}
          onCancel={() => setPendingRemove(null)}
        />
      ) : null}
    </>
  );
}

/** Atendimento do mês na For All Sales: leads, taxa de resposta e 1ª resposta. Sem vínculo, um traço. */
function AttendanceCell({ row }: { row?: { leadsReceived: number; answerRate: number | null; firstResponseMedian: number | null } }) {
  if (!row) return <span className="muted" title="Sem vínculo com a For All Sales">—</span>;
  return (
    <span className="num" style={{ fontSize: 12.5, lineHeight: 1.4, display: 'block' }}>
      <strong style={{ fontWeight: 500 }}>{formatNumber(row.leadsReceived)}</strong> {row.leadsReceived === 1 ? 'lead' : 'leads'}
      <span className="muted" style={{ display: 'block', fontSize: 11.5 }}>
        {formatRate(row.answerRate)} resp. · {formatDuration(row.firstResponseMedian)}
      </span>
    </span>
  );
}

/** Progresso do mês contra a meta mensal do membro; sem meta, um atalho para definir. */
function GoalCell({ goal, sold, onEdit }: { goal: number; sold: number; onEdit: () => void }) {
  if (goal <= 0) {
    return (
      <button type="button" className="link-btn muted" style={{ fontSize: 12 }} onClick={onEdit}>
        Definir meta
      </button>
    );
  }

  return (
    <button
      type="button"
      className="link-btn"
      style={{ display: 'block', width: '100%' }}
      title={`Vendido no mês: ${formatBRLShort(sold)} · clique para mudar a meta`}
      onClick={onEdit}
    >
      <ProgressBar value={sold} max={goal} height={14} />
      <span className="muted num" style={{ fontSize: 11, marginTop: 3, display: 'block' }}>
        {formatPercent(sold / goal)} de {formatBRLShort(goal)}
      </span>
    </button>
  );
}

function MemberModal({
  member, focusGoal, busy, onClose, onSubmit,
}: {
  member: User | null;
  /** Abre com o foco na meta em vez do nome. */
  focusGoal: boolean;
  busy: boolean;
  onClose: () => void;
  onSubmit: (payload: Record<string, unknown>) => Promise<void>;
}) {
  const [name, setName] = useState(member?.name ?? '');
  const [login, setLogin] = useState(member?.login ?? '');
  // No cadastro, o login acompanha o nome até alguém editá-lo à mão.
  const [loginTouched, setLoginTouched] = useState(Boolean(member));
  const [email, setEmail] = useState(member?.email ?? '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>(member?.role ?? 'MEMBER');
  const [active, setActive] = useState(member?.active ?? true);
  const [monthlyGoal, setMonthlyGoal] = useState(member?.monthlyGoal ?? 0);
  const [error, setError] = useState<string | null>(null);

  function changeName(value: string) {
    setName(value);
    if (!loginTouched) setLogin(suggestLogin(value));
  }

  function submit() {
    const normalizedLogin = login.trim().toLowerCase();
    if (name.trim().length < 2) return setError('Informe o nome completo.');
    if (!isValidLogin(normalizedLogin)) {
      return setError('Usuário: de 3 a 32 caracteres — letras sem acento, números, ponto, hífen ou sublinhado.');
    }
    if (email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      return setError('Informe um e-mail válido ou deixe o campo em branco.');
    }
    if (!member && !isStrongPassword(password)) {
      return setError(
        password.length < MIN_PASSWORD_LENGTH
          ? `A senha inicial precisa ter ao menos ${MIN_PASSWORD_LENGTH} caracteres.`
          : 'A senha precisa conter letras e números.',
      );
    }
    // A nova senha segue a mesma regra do servidor: tamanho, letras e números.
    if (member && password && !isStrongPassword(password)) {
      return setError(`A nova senha precisa ter ao menos ${MIN_PASSWORD_LENGTH} caracteres, com letras e números.`);
    }
    if (monthlyGoal < 0) return setError('A meta mensal não pode ser negativa.');

    setError(null);
    return void onSubmit({
      ...(member ? { id: member.id, active } : {}),
      name: name.trim(),
      login: normalizedLogin,
      // Vazio apaga o e-mail: ele é opcional.
      email: email.trim().toLowerCase(),
      role,
      monthlyGoal,
      ...(password ? { password } : {}),
    });
  }

  return (
    <Modal
      title={member ? `Editar ${member.name}` : 'Novo membro'}
      width={520}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={busy}>
            {busy ? <span className="spinner" /> : <IconCheck />} Salvar
          </button>
        </>
      }
    >
      <div className="grid grid2">
        <Field label="Nome *">
          <input
            className="inp"
            value={name}
            onChange={(event) => changeName(event.target.value)}
            autoFocus={!focusGoal}
          />
        </Field>
        <Field label="Usuário *" hint="É com ele que a pessoa entra.">
          <input
            className="inp"
            value={login}
            onChange={(event) => {
              setLoginTouched(true);
              setLogin(event.target.value.toLowerCase());
            }}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder="nome.sobrenome"
          />
        </Field>
      </div>

      <div className="grid grid2">
        <Field label="E-mail" hint="Opcional — também serve para entrar.">
          <input className="inp" type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
        </Field>
        <Field label="Papel">
          <select className="inp" value={role} onChange={(event) => setRole(event.target.value as Role)}>
            {ROLE_OPTIONS.map((option) => (
              <option key={option} value={option}>{ROLE_LABELS[option]}</option>
            ))}
          </select>
        </Field>
      </div>

      <PasswordField
        label={member ? 'Nova senha' : 'Senha inicial *'}
        hint={
          member
            ? 'Deixe em branco para manter a senha atual.'
            : `Mínimo ${MIN_PASSWORD_LENGTH} caracteres, com letras e números. "Copiar login" junta usuário, e-mail e senha numa mensagem pronta para mandar.`
        }
        value={password}
        onChange={setPassword}
        placeholder={member ? '••••••••' : ''}
        access={{ name, login: login.trim().toLowerCase(), email }}
      />

      <div className="grid grid2">
        <Field label="Meta mensal (R$)" hint="A mesma todo mês, pelo valor vendido nos comprovantes. Vazio = sem meta.">
          <MoneyInput value={monthlyGoal} onChange={setMonthlyGoal} autoFocus={focusGoal} onEnter={submit} />
        </Field>
      </div>

      {member ? (
        <label className="chip" style={{ alignSelf: 'flex-start' }}>
          <input type="checkbox" checked={active} onChange={(event) => setActive(event.target.checked)} />
          Acesso ativo
        </label>
      ) : null}

      {member ? (
        <div className="muted" style={{ fontSize: 12 }}>
          Cadastrado em {formatDate(member.createdAt.slice(0, 10))}
          {member.lastLoginAt ? ` · último acesso em ${formatDate(member.lastLoginAt.slice(0, 10))}` : ''}
        </div>
      ) : null}

      {error ? <div className="alert alert-error">{error}</div> : null}
    </Modal>
  );
}
